import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Bell,
  Building2,
  CalendarDays,
  ChevronDown,
  FileSignature,
  FileText,
  Globe,
  LogOut,
  Menu,
  ReceiptText,
  Scale,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';

const WAZIN_ICON_URL =
  'https://customer-assets.emergentagent.com/job_5ad4f013-9db1-4f9c-b371-b988a841f7e1/artifacts/nmtmq4a0_%D8%A7%D9%8A%D9%82%D9%88%D9%86%D8%A9%20%D8%A7%D9%84%D8%B4%D8%B9%D8%A7%D8%B1.png';

const businessHeader = {
  ar: {
    title: 'منصة وازن الرقمية',
    subtitle: 'Wazin Legal Platform',
    accountLabel: 'حساب أعمال نشط',
    crLabel: 'السجل التجاري',
    crNumber: '1010567890',
  },
  en: {
    title: 'Wazin Legal Platform',
    subtitle: 'Business Legal Workspace',
    accountLabel: 'Active Business Account',
    crLabel: 'Commercial Registration',
    crNumber: '1010567890',
  },
};

const tabLabels = {
  ar: {
    cases: 'القضايا',
    execution: 'التنفيذ',
    poas: 'الوكالات',
    invoices: 'الفواتير',
    team: 'الفريق',
  },
  en: {
    cases: 'Cases',
    execution: 'Enforcement',
    poas: 'POAs',
    invoices: 'Invoices',
    team: 'Team',
  },
};

const summaryCards = {
  ar: [
    { key: 'cases', label: 'القضايا النشطة', value: 2, sublabel: 'تحت المتابعة', color: '#2457B8', icon: Scale },
    { key: 'poas', label: 'الوكالات السارية', value: 2, sublabel: 'موثقة ومعتمدة', color: '#1F8A70', icon: FileSignature },
    { key: 'contracts', label: 'العقود السارية', value: 4, sublabel: 'محدثة وجاهزة', color: '#C8A45C', icon: ShieldCheck },
    { key: 'invoices', label: 'الفواتير المستحقة', value: 2, sublabel: '80,000 ريال', color: '#D98A00', icon: ReceiptText },
  ],
  en: [
    { key: 'cases', label: 'Active Cases', value: 2, sublabel: 'Under follow-up', color: '#2457B8', icon: Scale },
    { key: 'poas', label: 'Active POAs', value: 2, sublabel: 'Verified and valid', color: '#1F8A70', icon: FileSignature },
    { key: 'contracts', label: 'Live Contracts', value: 4, sublabel: 'Updated and ready', color: '#C8A45C', icon: ShieldCheck },
    { key: 'invoices', label: 'Pending Invoices', value: 2, sublabel: 'SAR 80,000', color: '#D98A00', icon: ReceiptText },
  ],
};

const businessCases = {
  ar: [
    {
      id: 'WZ-BIZ-2024-001',
      fileNumber: 'WZ-BIZ-2024-001',
      type: 'قضية تجارية',
      role: 'مدعي',
      counterpart: 'مؤسسة الريادة المتحدة',
      status: 'قيد المعالجة',
      updatedAt: '28 نوفمبر 2024',
      nextStep: 'مراجعة مذكرة الرد قبل الجلسة القادمة',
    },
    {
      id: 'WZ-BIZ-2024-002',
      fileNumber: 'WZ-BIZ-2024-002',
      type: 'طلب تنفيذ',
      role: 'طالب تنفيذ',
      counterpart: 'شركة الاتفاق المتقدمة',
      status: 'بانتظار إجراء',
      updatedAt: '25 نوفمبر 2024',
      nextStep: 'اعتماد المستندات المرفوعة قبل إحالتها للقضاء',
    },
    {
      id: 'WZ-BIZ-2023-045',
      fileNumber: 'WZ-BIZ-2023-045',
      type: 'نزاع تعاقدي',
      role: 'مدعى عليه',
      counterpart: 'شركة الإمداد الاستراتيجي',
      status: 'مغلقة',
      updatedAt: '15 سبتمبر 2024',
      nextStep: 'تم إغلاق الملف وتسليم النتائج النهائية',
    },
  ],
  en: [
    {
      id: 'WZ-BIZ-2024-001',
      fileNumber: 'WZ-BIZ-2024-001',
      type: 'Commercial Dispute',
      role: 'Claimant',
      counterpart: 'United Pioneer Est.',
      status: 'In Progress',
      updatedAt: '28 Nov 2024',
      nextStep: 'Review the reply memo before the next hearing',
    },
    {
      id: 'WZ-BIZ-2024-002',
      fileNumber: 'WZ-BIZ-2024-002',
      type: 'Enforcement Request',
      role: 'Applicant',
      counterpart: 'Advanced Agreement Co.',
      status: 'Awaiting Action',
      updatedAt: '25 Nov 2024',
      nextStep: 'Approve uploaded documents before filing',
    },
    {
      id: 'WZ-BIZ-2023-045',
      fileNumber: 'WZ-BIZ-2023-045',
      type: 'Contractual Dispute',
      role: 'Respondent',
      counterpart: 'Strategic Supply Co.',
      status: 'Closed',
      updatedAt: '15 Sep 2024',
      nextStep: 'File closed and final outcome delivered',
    },
  ],
};

const sectionText = {
  ar: {
    casesTitle: 'قضايا الشركة',
    casesDescription: 'تابع الملفات القضائية للشركة، حالاتها، وآخر إجراء تم عليها بوضوح.',
    searchPlaceholder: 'ابحث برقم القضية أو اسم الطرف',
    fileNumber: '#رقم الملف',
    caseType: 'نوع القضية',
    role: 'الصفة',
    counterpart: 'الطرف الآخر',
    status: 'الحالة',
    updatedAt: 'آخر تحديث',
    nextStep: 'الخطوة القادمة',
    file: 'الملف',
    openFile: 'فتح الملف',
    noCases: 'لا توجد ملفات لعرضها في هذا القسم حالياً.',
    menu: 'القائمة',
    profile: 'الملف الشخصي',
    updateAccount: 'تحديث الحساب',
    switchAccount: 'تبديل الصلاحية',
    logout: 'تسجيل الخروج',
  },
  en: {
    casesTitle: 'Company Cases',
    casesDescription: 'Track the company legal files, status changes, and the latest action taken on each file.',
    searchPlaceholder: 'Search by case number or party name',
    fileNumber: 'File #',
    caseType: 'Case Type',
    role: 'Role',
    counterpart: 'Counterparty',
    status: 'Status',
    updatedAt: 'Last Updated',
    nextStep: 'Next Step',
    file: 'File',
    openFile: 'Open File',
    noCases: 'No files are available in this section at the moment.',
    menu: 'Menu',
    profile: 'Profile',
    updateAccount: 'Update account',
    switchAccount: 'Switch access',
    logout: 'Log out',
  },
};

const statusClasses = {
  ar: {
    'قيد المعالجة': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    'بانتظار إجراء': 'bg-amber-50 text-amber-700 border border-amber-200',
    متأخرة: 'bg-rose-50 text-rose-700 border border-rose-200',
    مغلقة: 'bg-slate-100 text-slate-600 border border-slate-200',
  },
  en: {
    'In Progress': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    'Awaiting Action': 'bg-amber-50 text-amber-700 border border-amber-200',
    Overdue: 'bg-rose-50 text-rose-700 border border-rose-200',
    Closed: 'bg-slate-100 text-slate-600 border border-slate-200',
  },
};

const SimpleListSection = ({ title, items, actionLabel, colorClass = 'text-[#0F2742]' }) => (
  <div className="rounded-[28px] border border-[#D9E5F3] bg-white p-6 shadow-[0_18px_40px_rgba(15,39,66,0.08)]">
    <div className="flex items-center justify-between">
      <h3 className={`text-xl font-black ${colorClass}`}>{title}</h3>
      <button className="rounded-full border border-[#D9E5F3] px-4 py-2 text-sm font-bold text-[#0F2742] transition hover:border-[#2457B8] hover:text-[#2457B8]">
        {actionLabel}
      </button>
    </div>
    <div className="mt-5 space-y-3">
      {items.map((item) => (
        <div key={item.id} className="flex items-center justify-between rounded-2xl border border-[#ECF1F8] bg-[#FBFCFE] px-4 py-4">
          <div className="text-right">
            <div className="text-base font-bold text-[#0F2742]">{item.title}</div>
            <div className="mt-1 text-sm text-slate-500">{item.meta}</div>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#2457B8] shadow-sm">{item.badge}</span>
        </div>
      ))}
    </div>
  </div>
);

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en');
  const locale = isEnglish ? 'en' : 'ar';

  const [activeTab, setActiveTab] = useState('cases');
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const tabs = tabLabels[locale];
  const header = businessHeader[locale];
  const cardData = summaryCards[locale];
  const caseRows = businessCases[locale];
  const contentText = sectionText[locale];

  const filteredCases = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return caseRows;
    return caseRows.filter((row) =>
      [row.fileNumber, row.type, row.counterpart].some((value) => value.toLowerCase().includes(query))
    );
  }, [caseRows, searchQuery]);

  const renderCasesTable = () => (
    <div className="rounded-[32px] border border-[#D7E3F2] bg-white/95 p-6 shadow-[0_24px_48px_rgba(15,39,66,0.08)] backdrop-blur-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="text-right">
          <h2 className="text-[2rem] font-black tracking-tight text-[#0F2742]">{contentText.casesTitle}</h2>
          <p className="mt-2 text-sm text-slate-500">{contentText.casesDescription}</p>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9BAEC8]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={contentText.searchPlaceholder}
            className="h-14 w-full rounded-full border border-[#D7E3F2] bg-[#FBFCFE] pr-12 pl-5 text-right text-sm font-semibold text-[#0F2742] outline-none transition focus:border-[#2457B8] focus:bg-white"
          />
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-[26px] border border-[#E3EAF4] bg-white">
        <div className="hidden grid-cols-[1.15fr_1.2fr_0.9fr_1.2fr_1fr_1fr_1.7fr_0.55fr] gap-4 border-b border-[#E8EEF7] bg-[#F8FAFD] px-6 py-4 text-sm font-black text-[#6A7E9B] lg:grid">
          <div className="text-right">{contentText.fileNumber}</div>
          <div className="text-right">{contentText.caseType}</div>
          <div className="text-right">{contentText.role}</div>
          <div className="text-right">{contentText.counterpart}</div>
          <div className="text-right">{contentText.status}</div>
          <div className="text-right">{contentText.updatedAt}</div>
          <div className="text-right">{contentText.nextStep}</div>
          <div className="text-center">{contentText.file}</div>
        </div>

        {filteredCases.length ? (
          filteredCases.map((row) => (
            <div
              key={row.id}
              className="border-b border-[#EEF3F9] px-6 py-5 last:border-b-0 transition hover:bg-[#FBFDFF]"
            >
              <div className="grid gap-4 lg:grid-cols-[1.15fr_1.2fr_0.9fr_1.2fr_1fr_1fr_1.7fr_0.55fr] lg:items-center">
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.fileNumber}</div>
                  <div className="text-xl font-black tracking-tight text-[#0F2742]">{row.fileNumber}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.caseType}</div>
                  <div className="text-xl font-black text-[#0F2742]">{row.type}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.role}</div>
                  <div className="text-base font-bold text-[#4B5F7C]">{row.role}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.counterpart}</div>
                  <div className="text-base font-bold text-[#0F2742]">{row.counterpart}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.status}</div>
                  <span className={`inline-flex rounded-full px-4 py-2 text-sm font-bold ${statusClasses[locale][row.status] || statusClasses[locale].Closed}`}>
                    {row.status}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.updatedAt}</div>
                  <div className="text-base font-semibold text-[#6A7E9B]">{row.updatedAt}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-400 lg:hidden">{contentText.nextStep}</div>
                  <div className="text-base font-semibold leading-7 text-[#445873]">{row.nextStep}</div>
                </div>
                <div className="flex justify-center lg:justify-center">
                  <button
                    onClick={() => navigate(`/business/case/${row.id}`)}
                    className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D8E2F0] bg-white text-[#0F2742] shadow-[0_8px_18px_rgba(15,39,66,0.08)] transition hover:-translate-y-0.5 hover:border-[#2457B8] hover:text-[#2457B8]"
                    title={contentText.openFile}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="px-6 py-16 text-center text-[#6A7E9B]">{contentText.noCases}</div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'cases') return renderCasesTable();

    const sections = {
      execution: {
        title: locale === 'ar' ? 'طلبات التنفيذ' : 'Enforcement Requests',
        action: locale === 'ar' ? 'عرض جميع الطلبات' : 'View all requests',
        items: [
          { id: 1, title: locale === 'ar' ? 'طلب تنفيذ مالي' : 'Financial Enforcement', meta: locale === 'ar' ? 'قيد الإجراء لدى الدائرة المختصة' : 'Under review by the competent circuit', badge: '2' },
          { id: 2, title: locale === 'ar' ? 'تنفيذ سند لأمر' : 'Promissory Note Enforcement', meta: locale === 'ar' ? 'بانتظار اعتماد المستندات' : 'Awaiting document approval', badge: '1' },
        ],
      },
      poas: {
        title: locale === 'ar' ? 'الوكالات' : 'Powers of Attorney',
        action: locale === 'ar' ? 'عرض جميع الوكالات' : 'View all POAs',
        items: [
          { id: 1, title: locale === 'ar' ? 'وكالة قضائية عامة' : 'General Litigation POA', meta: locale === 'ar' ? 'سارية حتى 10 يناير 2025' : 'Valid until 10 Jan 2025', badge: locale === 'ar' ? 'سارية' : 'Active' },
          { id: 2, title: locale === 'ar' ? 'وكالة خاصة بالعقود' : 'Contracts POA', meta: locale === 'ar' ? 'سارية حتى 31 ديسمبر 2024' : 'Valid until 31 Dec 2024', badge: locale === 'ar' ? 'سارية' : 'Active' },
        ],
      },
      invoices: {
        title: locale === 'ar' ? 'الفواتير والمدفوعات' : 'Invoices & Payments',
        action: locale === 'ar' ? 'عرض السجل المالي' : 'View billing history',
        items: [
          { id: 1, title: locale === 'ar' ? 'فاتورة مستحقة - نوفمبر' : 'Outstanding Invoice - November', meta: locale === 'ar' ? 'قيمة الفاتورة 50,000 ريال' : 'Invoice amount SAR 50,000', badge: locale === 'ar' ? 'مستحقة' : 'Due' },
          { id: 2, title: locale === 'ar' ? 'دفعة مجدولة - ديسمبر' : 'Scheduled Payment - December', meta: locale === 'ar' ? 'موعد السداد 15 ديسمبر 2024' : 'Due on 15 Dec 2024', badge: locale === 'ar' ? 'مجدولة' : 'Scheduled' },
        ],
      },
      team: {
        title: locale === 'ar' ? 'فريق الحساب' : 'Account Team',
        action: locale === 'ar' ? 'إدارة الفريق' : 'Manage team',
        items: [
          { id: 1, title: locale === 'ar' ? 'مدير الحساب القانوني' : 'Legal Account Manager', meta: locale === 'ar' ? 'متابع لجميع ملفات الشركة القانونية' : 'Oversees all legal files', badge: locale === 'ar' ? 'أساسي' : 'Primary' },
          { id: 2, title: locale === 'ar' ? 'ممثل الشركة' : 'Company Representative', meta: locale === 'ar' ? 'مخول بإدارة الطلبات والموافقات' : 'Authorized for requests and approvals', badge: locale === 'ar' ? 'مفعل' : 'Active' },
        ],
      },
    };

    const section = sections[activeTab];
    return <SimpleListSection title={section.title} items={section.items} actionLabel={section.action} />;
  };

  return (
    <div className="min-h-screen bg-[#F4F8FD]">
      <div className="pointer-events-none fixed inset-y-0 left-0 w-64 bg-[radial-gradient(circle_at_left,rgba(36,87,184,0.06),transparent_70%)]" />
      <div className="pointer-events-none fixed inset-y-0 right-0 w-64 bg-[radial-gradient(circle_at_right,rgba(15,39,66,0.06),transparent_70%)]" />

      <div className="mx-auto max-w-7xl px-4 pb-10 pt-6 sm:px-6 lg:px-8">
        <div className="relative overflow-visible rounded-[34px] bg-gradient-to-r from-[#163C70] via-[#2457B8] to-[#112C55] px-8 py-7 text-white shadow-[0_34px_80px_rgba(17,44,85,0.26)]">
          <img
            src={WAZIN_ICON_URL}
            alt=""
            className="pointer-events-none absolute left-8 top-1/2 h-72 w-72 -translate-y-1/2 object-contain opacity-[0.11]"
          />

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="order-2 flex items-center gap-3 lg:order-1">
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="flex items-center gap-3 rounded-[22px] border border-white/18 bg-white/10 px-5 py-4 text-sm font-black shadow-[0_14px_28px_rgba(15,39,66,0.16)] backdrop-blur-md transition hover:bg-white/16"
                >
                  <span>{contentText.menu}</span>
                  <Menu className="h-5 w-5" />
                </button>

                {menuOpen && (
                  <div className="absolute left-0 top-[calc(100%+12px)] z-[90] w-72 overflow-hidden rounded-[28px] border border-white/15 bg-white/95 p-2 text-right shadow-[0_24px_60px_rgba(15,39,66,0.28)] backdrop-blur-xl">
                    <button className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-[#0F2742] transition hover:bg-[#F7F9FB]">
                      <Users className="h-5 w-5 text-[#2457B8]" />
                      <span className="font-bold">{contentText.profile}</span>
                    </button>
                    <button className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-[#0F2742] transition hover:bg-[#F7F9FB]">
                      <Settings className="h-5 w-5 text-[#2457B8]" />
                      <span className="font-bold">{contentText.updateAccount}</span>
                    </button>
                    <button className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-[#0F2742] transition hover:bg-[#F7F9FB]">
                      <Building2 className="h-5 w-5 text-[#2457B8]" />
                      <span className="font-bold">{contentText.switchAccount}</span>
                    </button>
                    <div className="my-2 h-px bg-[#E6ECF5]" />
                    <button
                      onClick={() => {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        navigate('/login');
                      }}
                      className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-red-600 transition hover:bg-red-50"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-bold">{contentText.logout}</span>
                    </button>
                  </div>
                )}
              </div>

              <button className="relative flex h-14 w-14 items-center justify-center rounded-[20px] border border-white/18 bg-white/10 shadow-[0_14px_28px_rgba(15,39,66,0.16)] backdrop-blur-md transition hover:bg-white/16">
                <Bell className="h-5 w-5" />
                <span className="absolute right-0 top-0 flex h-6 w-6 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full border-2 border-[#163C70] bg-[#F3E2BA] text-xs font-black text-[#163C70]">
                  2
                </span>
              </button>

              <button
                onClick={() => i18n.changeLanguage(isEnglish ? 'ar' : 'en')}
                className="flex items-center gap-3 rounded-[22px] border border-white/18 bg-white/10 px-5 py-4 text-sm font-black shadow-[0_14px_28px_rgba(15,39,66,0.16)] backdrop-blur-md transition hover:bg-white/16"
              >
                <span>{isEnglish ? 'AR | EN' : 'EN | العربية'}</span>
                <Globe className="h-5 w-5" />
              </button>
            </div>

            <div className="order-1 flex items-center gap-5 lg:order-2">
              <div className="text-right">
                <div className="text-[1.55rem] font-black leading-none text-[#F2DFB0] lg:text-[1.9rem]">{header.title}</div>
                <div className="mt-2 text-base font-semibold tracking-wide text-white/90 lg:text-[1.15rem]">{header.subtitle}</div>
                <div className="mt-4 flex items-center justify-end gap-3">
                  <span className="rounded-full border border-[#39B98A]/45 bg-[#1F8A70]/18 px-4 py-1.5 text-sm font-black text-[#6EF0C2]">
                    {header.accountLabel}
                  </span>
                  <span className="text-sm text-white/75">{header.crLabel}:</span>
                  <span className="rounded-full border border-white/15 bg-white/12 px-4 py-1.5 text-sm font-black text-white">
                    {header.crNumber}
                  </span>
                </div>
              </div>
              <div className="flex h-24 w-24 items-center justify-center rounded-[26px] border-2 border-[#C8A45C] bg-white shadow-[0_18px_40px_rgba(15,39,66,0.20)]">
                <img src={WAZIN_ICON_URL} alt="Wazin" className="h-14 w-14 object-contain" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-[30px] border border-[#D9E3F0] bg-white/95 p-3 shadow-[0_18px_38px_rgba(15,39,66,0.08)]">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {cardData.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.key}
                  onClick={() => setActiveTab(card.key === 'contracts' ? 'invoices' : card.key)}
                  className="flex items-center justify-between rounded-[22px] border border-[#E3EAF4] bg-white px-4 py-3 text-right transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(15,39,66,0.08)]"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}CC)` }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2.3} />
                    </div>
                    <div>
                      <div className="text-sm font-black text-[#0F2742]">{card.label}</div>
                      <div className="mt-1 text-xs text-slate-500">{card.sublabel}</div>
                    </div>
                  </div>
                  <span className="text-[2rem] font-black" style={{ color: card.color }}>
                    {card.value}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-7 rounded-[30px] border border-[#D9E3F0] bg-white/96 p-3 shadow-[0_18px_38px_rgba(15,39,66,0.08)]">
          <div className="flex flex-wrap gap-2">
            {Object.entries(tabs).map(([key, label]) => {
              const iconMap = {
                cases: FileText,
                execution: ShieldCheck,
                poas: FileSignature,
                invoices: ReceiptText,
                team: Users,
              };
              const Icon = iconMap[key];
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 rounded-[18px] px-5 py-3 text-sm font-black transition ${
                    activeTab === key
                      ? 'bg-gradient-to-r from-[#163C70] to-[#2457B8] text-white shadow-[0_12px_24px_rgba(36,87,184,0.20)]'
                      : 'text-[#445873] hover:bg-[#F6F9FD]'
                  }`}
                >
                  <Icon className="h-4.5 w-4.5" />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8">{renderContent()}</div>
      </div>
    </div>
  );
};

export default BusinessDashboard;
